import { useState } from "react";
import { useAppSelector } from "../../../app/hooks";
import ExportIcon from "../../../assets/ExportIcon";
import PerformanceIcon from "../../../assets/PerformanceIcon";
import PlagiarismIcon from "../../../assets/PlagiarismIcon";
import ProgressIcon from "../../../assets/ProgressIcon";
import PromptsIcon from "../../../assets/PromptsIcon";
import UsageIcon from "../../../assets/UsageIcon";
import { SelectDevelopmentView } from "../../../components/input/SelectDevelopmentView";
import RtkqResultViewer from "../../../components/RtkqResultViewer";
import { ReportBlock, ReportBlockConfig } from "../shared/ReportBlock";
import { DownloadButton } from "../shared/DownloadButton";
import { useReportsPageQuery } from "../reportsApiSlice";
import {
	USAGE_SUMMARY,
	USAGE_WRITING,
	USAGE_PEER_REVIEWS,
	USAGE_LESSONS,
} from "./usage/UsageReport";
import {
	CLASS_TRAIT_AND_TOTAL_SCORES,
	CLASS_WRITING_GENRE_SCORES,
	CLASS_CR_SCORES,
} from "./performance/PerformanceReport";
import { CLASS_PROMPT_ANALYSIS, CLASS_CR_PROMPT_ANALYSIS } from "./prompt/PromptReport";
import { CLASS_AVERAGE_MONTHLY_SCORES } from "./progress/ProgressReport";
import { ESSAY_DRAFT_HISTORY_SECTION } from "./progress/essayDraftHistory/EssayDraftHistory";

export default function CourseReports() {
	const [view, setView] = useState<string>("legacy");

	const { config, aspUrl } = useAppSelector((state) => state.site);
	const { course } = useAppSelector((state) => state.user);

	const result = useReportsPageQuery();
	const data = result.data;

	const downloadUrl = `${aspUrl}/Report/DownloadDraftsFilteredByCourse${course ? `?courseId=${course.id}` : ""}`;

	const reportBlocks: ReportBlockConfig[] = [
		{
			heading: "Usage",
			Icon: UsageIcon,
			link: "/reports/course/usage",
			links: [
				{
					text: "Summary",
					section: USAGE_SUMMARY,
				},
				{
					text: "Writing",
					section: USAGE_WRITING,
				},
				{
					text: "Peer Reviews",
					section: USAGE_PEER_REVIEWS,
				},
				{
					text: "Lessons",
					section: USAGE_LESSONS,
				},
			],
		},
		{
			heading: "Performance",
			Icon: PerformanceIcon,
			link: "/reports/course/performance",
			links: [
				{
					text: `${config?.isUtahComposeSite ? "Analytical" : "Trait"} and Total Scores`,
					section: CLASS_TRAIT_AND_TOTAL_SCORES,
				},
				{
					text: "Constructed Response Scores",
					section: CLASS_CR_SCORES,
				},
				{
					text: "Writing Genre Scores",
					section: CLASS_WRITING_GENRE_SCORES,
				},
			],
		},
		{
			heading: "Prompts",
			Icon: PromptsIcon,
			link: "/reports/course/prompt",
			links: [
				{
					text: "Prompt Analysis",
					section: CLASS_PROMPT_ANALYSIS,
				},
				{
					text: "Constructed Response Analysis",
					section: CLASS_CR_PROMPT_ANALYSIS,
				},
			],
		},
		{
			heading: "Progress",
			Icon: ProgressIcon,
			link: "/reports/course/progress",
			links: [
				{
					text: "Average Monthly Scores",
					section: CLASS_AVERAGE_MONTHLY_SCORES,
				},
				{
					text: "Essay Draft History",
					section: ESSAY_DRAFT_HISTORY_SECTION,
				},
			],
		},
		{
			heading: "Plagiarism",
			Icon: PlagiarismIcon,
			link: "/reports/course/plagiarism",
			links: [
				{
					text: "Plagiarism Analysis",
				},
			],
		},
		{
			heading: "Export",
			Icon: ExportIcon,
			children: (
				<>
					<p>
						Would you like to create your own course reports? Click <b>Download</b> to
						save all class data to a file that can be opened by a spreadsheet
						application on your computer.
					</p>
					<DownloadButton className="btn btn-primary mt-4" url={downloadUrl} />
				</>
			),
		},
	];

	return (
		<section>
			<div className="flex justify-between">
				<div className="w-3/4">
					<h1>Course Reports</h1>
				</div>
				<SelectDevelopmentView
					value={view}
					setValue={(v) => setView(v as string)}
					simpleListBoxId="viewFilter"
					options={[
						{ value: "legacy", label: "Legacy" },
						{ value: "raw", label: "Raw Data" },
					]}
					fieldClasses="w-64"
				/>
			</div>
			{view == "legacy" && data && (
				<div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-4">
					{reportBlocks.map((b, index) => {
						if (!data.showPlagiarismReports && b.heading === "Plagiarism") return "";
						return <ReportBlock block={b} key={index} />;
					})}
				</div>
			)}
			{view == "raw" && <RtkqResultViewer heading="Report View Data" result={result} />}
		</section>
	);
}
